import { Button, Link, tokens } from "@blurb/codex-react";
import { Box } from "@mantine/core";
import { IconV3 } from "@rpi/openapi-core";
import React from "react";
import { isRouteErrorResponse, useRouteError } from "react-router-dom";
import { Body, Heading } from "../../components/Typography";
import { useNavigateRoute } from "../../hooks/useNavigateRoute";
import { awsRum } from "../../rum/cloudwatchRum";
import { PublicEmail } from "../../utils/link.util";
import { MailTo } from "../../utils/mail.util";
import { isNotFoundGraphQLError } from "../../utils/query.util";
import { Error404View } from "./Error404View";
import { ErrorStateLayout } from "./ErrorStateLayout";

const isNotFoundError = (error: unknown): boolean =>
  (isRouteErrorResponse(error) && error.status === 404) || isNotFoundGraphQLError(error);

interface RouteErrorViewProps {
  /** Is root page. */
  root?: boolean;
}

/**
 * Router `errorElement` that shows genuine 404 responses as "Page Not Found" and
 * reports any other thrown error to CloudWatch RUM before rendering a crash screen.
 */
export const RouteErrorView: React.FC<RouteErrorViewProps> = ({ root = true }) => {
  const error = useRouteError();
  const navigateRoute = useNavigateRoute();
  const isNotFound = isNotFoundError(error);

  React.useEffect(() => {
    if (isNotFound) return;
    awsRum?.recordError(error);
    console.error("Route error:", error);
  }, [error, isNotFound]);

  if (isNotFound) {
    return <Error404View root={root} />;
  }

  return (
    <ErrorStateLayout root={root} data-testid="route-error-view">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: tokens.spacing[2],
          borderRadius: tokens.radius.full,
          backgroundColor: tokens.color.semantic.bg.dangerSubtlest,
        }}
      >
        <IconV3 icon="error" size={40} color={tokens.color.semantic.icon.danger} />
      </Box>
      <Heading as="h1" size="9xl" weight="medium">
        Something went wrong
      </Heading>
      <Body size="base">
        Please try again later. If the problem persists, please contact our support at{" "}
        <Link href={MailTo({ email: PublicEmail.SUPPORT })}>{PublicEmail.SUPPORT}</Link>.
      </Body>
      <Button variant="filled" intent="primary" size="large" onClick={() => navigateRoute(0)}>
        Try again
      </Button>
    </ErrorStateLayout>
  );
};
